<template>
  <div>
    <div class="container">
      <div class="container">

        <!-- PAGE HEADER -->
        <div class="section-head" :style="{ '--accent': '#4F7EB3' }">
          <div class="head-left">
            <h3 class="title">
              {{ flowStore.currentFlow.FlowName }}
              <button
                v-if="!isCameraRunning"
                type="button"
                class="icon-inline"
                @click="openNameEdit"
                :title="$t('cameras.flowSettings.editName')"
              >
                <i class="bi bi-pencil"></i>
              </button>
            </h3>

            <!-- inline name editor -->
            <div v-if="editName" class="name-edit">
              <input
                id="name"
                type="text"
                class="input center"
                v-model="flowStore.currentFlow.FlowName"
                :placeholder="$t('cameras.flowSettings.flowName')"
              />
            </div>

            <!-- status chip if running -->
            <div v-if="isCameraRunning" class="banner-warn">
              <b>{{ $t('cameras.flowSettings.statusLabel') }}</b> {{ $t('cameras.flowSettings.runningNoChanges') }}
            </div>
          </div>

          <div class="head-actions">
            <button type="button" class="btn-outline" @click="backHome">
              <i class="bi bi-camera-video-fill"></i>
              {{ $t('cameras.flowSettings.backToStreams') }}
            </button>
          </div>
        </div>

        <!-- SETTINGS CARD -->
        <div class="card kpi-accent" :style="{ '--accent': '#4F7EB3' }">
          <div class="card-header form-head">
            <h4 class="form-title">{{ $t('cameras.flowSettings.sourceAndZones') }}</h4>
          </div>

          <div class="card-body">
            <!-- camera stream info -->
            <div class="read-block" v-if="flowStore.currentFlow.InputTypeName==='Camera Stream'">
              <label class="read-label">{{ $t('cameras.stream.title') }}</label>
              <p class="read-value muted">{{ flowStore.currentFlow.CameraStreamLink }}</p>
            </div>

            <!-- loader -->
            <div class="loader-wrap" v-if="isLoading">
              <div class="loader"></div>
            </div>

            <!-- detection zones editor -->
            <div class="dz-wrap">
              <detection-zone
                :stream_link="cameraStreamLink"
                :stream_id="cameraStreamId"
                :captured_frame="capturedFrame"
                :detection_zone="DetectionZone"
                :is_camera_running="isCameraRunning"
                :flow_id="flowStore.currentFlow.FlowId"
                @detectionzones="on_detectionzone_logic"
              />
            </div>
          </div>

          <!-- FOOTER ACTIONS -->
          <div class="form-actions">
            <div v-if="isSaving" class="saving hide-on-small">
              <div class="saver"></div><span>{{ $t('cameras.actions.saving') }}</span>
            </div>

            <!-- Settings step 2e: a moved or turned camera changes what every zone sees,
                 also when the zones still fit the new picture. Save then asks per zone. -->
            <label class="camera-moved hide-on-small"
                   :title="$t('cameras.flowSettings.cameraMovedHint')">
              <input type="checkbox" v-model="cameraMoved" />
              {{ $t('cameras.flowSettings.cameraMoved') }}
            </label>

            <button
              type="button"
              class="btn-solid ok hide-on-small"
              :disabled="!hasChanges && !cameraMoved"
              @click="saveEditedFlow"
              :title="$t('cameras.actions.saveChanges')"
            >
              <i class="bi bi-save"></i> {{ $t('cameras.actions.save') }}
            </button>

            <button
              type="button"
              class="btn-solid danger hide-on-small"
              :disabled="isCameraRunning"
              @click="deleteFlow"
              :title="$t('cameras.flowSettings.deleteFlow')"
            >
              <i class="bi bi-trash"></i> {{ $t('cameras.actions.delete') }}
            </button>

            <button type="button" class="btn-outline" @click="backHome">
              <i class="bi bi-camera-video-fill"></i> {{ $t('cameras.actions.back') }}
            </button>
          </div>
        </div>

      </div>
    </div>
    <!-- "What happened at this spot?" — before a save that changes a zone's name, type,
         area or counting line (settings step 2). -->
    <ZoneChangeQuestion v-if="changeQuestion" :items="changeQuestion.items"
                        :options="changeQuestion.options" :lead="changeQuestion.lead"
                        :all-at-once="changeQuestion.allAtOnce"
                        @answer="answerChangeQuestion" @cancel="answerChangeQuestion(null)" />
  </div>
</template>

<script>
import axios from 'axios'

import { onBeforeMount, ref, computed} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

import { useFlowStore } from '@/stores/FlowStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { parseDeviceUrl } from '@/api/deviceUrl'
import { useUserStore } from '@/stores/UserStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { useCameraStreamStore } from '@/stores/CameraStreamStore'

import DropdownInputType from './DropdownInputType.vue'
import DetectionZone from './DetectionZone.vue'
import ZoneChangeQuestion from './zones/ZoneChangeQuestion.vue'
import { whatChanged, suggestedKind, CHANGE_OPTIONS, CAMERA_MOVED_OPTIONS } from '@/zones/changeQuestion'
import { ZONE_TYPE_NAMES } from '@/zones/changesToday'

export default {
  components: {
    DropdownInputType,
    DetectionZone,
    ZoneChangeQuestion,
    },

  setup() {
    const toast = useToast()
    const { t } = useI18n()

    // The change question: open while a save waits for the answers.
    const changeQuestion = ref(null)
    const cameraMoved = ref(false)        // "The camera was moved" (settings step 2e)
    const askChangeKinds = (replaced) => new Promise((resolve) => {
      changeQuestion.value = {
        resolve,
        options: cameraMoved.value ? CAMERA_MOVED_OPTIONS : CHANGE_OPTIONS,
        lead: cameraMoved.value
          ? t('cameras.flowSettings.leadCameraMoved')
          : t('cameras.flowSettings.leadZoneChange'),
        allAtOnce: cameraMoved.value,
        items: replaced.map(({ oldId, oldZone, zone }) => ({
          oldId,
          title: zone.DetectionZoneName,
          changes: whatChanged(oldZone || {}, zone, (id) => (ZONE_TYPE_NAMES[id] ? zoneTypeLabel(ZONE_TYPE_NAMES[id]) : t('cameras.flowSettings.typeId', { id }))),
          suggested: suggestedKind(oldZone || {}, zone),
        })),
      }
    })
    const answerChangeQuestion = (answers) => {
      const q = changeQuestion.value
      changeQuestion.value = null
      if (q) q.resolve(answers)
    }

    const flowStore = useFlowStore()
    const companyStore = useCompanyStore()
    const userStore = useUserStore()
    const detectionzones = useDetectionZoneStore()
    const CameraStreamStore = useCameraStreamStore()

    const cameraStreamLink = ref('')
    const cameraStreamId = ref(null)     // for /detection_zone_frame (Stage 4)
    const isCameraRunning = ref()

    const route = useRoute()
    const router = useRouter()

    const capturedFrame = ref(null)
    const capturedFrameString = ref('')
    const DetectionZone = ref({})

    const originalFlowName = ref('')
    const originalDetectionZones = ref({})

    const isLoading = ref(null)
    const isSaving = ref(null)

    const showNotification = ref(null)


    onBeforeMount(async () => {
      try {
        await flowStore.fetchCurrentFlow(parseInt(route.params.id))

        if (flowStore.currentFlow.FlowId !== undefined && flowStore.currentFlow.CameraStreamLink !== undefined) {
          isLoading.value = true
          await CameraStreamStore.fetchInputCameraStreambyFlowId(flowStore.currentFlow.FlowId)
          isCameraRunning.value = CameraStreamStore.inputcamerastream.StreamStatus
          cameraStreamLink.value = flowStore.currentFlow.CameraStreamLink
          originalFlowName.value = flowStore.currentFlow.FlowName

          // fetchInputCameraStreambyFlowId (above) already put the row in the
          // store, so the id costs no extra round trip.
          cameraStreamId.value = CameraStreamStore.inputcamerastream.CameraStreamId
          await captureFrame(cameraStreamId.value)
          DetectionZone.value = flowStore.currentFlow.DetectionZones

          if (DetectionZone.value) {
            originalDetectionZones.value = JSON.stringify(
              Object.entries(DetectionZone.value).map(([name, zone]) => ({
                name,
                type: zone.type,
                points: zone.points,
                tripwire: zone.tripwire || null,
                tripwires: Array.isArray(zone.tripwires) ? zone.tripwires : [],
                settings: zone.settings
              }))
            )
          }
        }
      } catch (error) {
        console.error('Error during onBeforeMount:', error);
      }
    })

    // Takes the stream's ID, not its URL. The edge resolves the URL from its own
    // CameraStreams row, so the browser can no longer name what the device opens
    // — that free-form URL was an SSRF sink (known-gaps #3 Stage 4).
    const captureFrame = async (cameraStreamId) => {
      if (cameraStreamId === undefined || cameraStreamId === null) {
        // No row yet means nothing to capture. Previously this sent an empty
        // link and let the device fail; now it is simply not a request.
        return
      }
      try {
        const { httpBase, proxied } = parseDeviceUrl(companyStore.company.SocketioDeviceURL)

        const response = await axios.get(`${httpBase}/detection_zone_frame`, {
          params: { cameraStreamId: cameraStreamId },
          responseType: 'blob',
          withCredentials: proxied
        })
        const blob = new Blob([response.data], { type: 'image/jpeg' })
        const urlCreator = window.URL || window.webkitURL
        const imageUrl = urlCreator.createObjectURL(blob)
        capturedFrame.value = imageUrl
        isLoading.value = false
        toast.success(t('cameras.flowSettings.frameCaptured'))
      } catch (error) {
        console.error('Frame capture failed:', error)
        toast.error(t('cameras.flowSettings.frameFailed', { message: error.message }))
        isLoading.value = false
      }
    }

    const onCancel = () => {
      console.log('User cancelled the loader.')
    }

    const editName = ref(false)
    const openNameEdit = () => {
      if (!(editName.value)) {
        editName.value = true
      } else {
        editName.value = false
      }
    }

    const on_detectionzone_logic = (value) => {
        DetectionZone.value = value
    }

    // Check if there are unsaved changes
    const hasChanges = computed(() => {
      const currentFlowName = flowStore.currentFlow.FlowName
      const currentDetectionZones = JSON.stringify(
        Object.entries(DetectionZone.value || {}).map(([name, zone]) => ({
          name,
          type: zone.type,
          points: zone.points,
          tripwire: zone.tripwire || null,
          tripwires: Array.isArray(zone.tripwires) ? zone.tripwires : [],
          settings: zone.settings
        }))
      )

      return (
        currentFlowName !== originalFlowName.value ||
        currentDetectionZones !== originalDetectionZones.value
      )
    })

    const saveEditedFlow = async () => {
      try {
        isSaving.value = true
        try {
          await detectionzones.editDetectionZoneToFlow(flowStore.currentFlow.FlowId, DetectionZone.value,
                                                      { askChangeKinds, cameraMoved: cameraMoved.value })
        } catch (error) {
          if (error?.cancelled) {            // the change question was cancelled: nothing saved
            isSaving.value = false
            return
          }
          // Say what went wrong (e.g. a name another zone on this camera has) and stay
          // on the page, edits intact, so it can be corrected and saved again.
          console.error("Error updating DetectionZone table:", error)
          const msg = error?.response?.data?.message || error?.response?.data?.error
          toast.error(msg
            ? t('cameras.flowSettings.zonesNotSaved', { message: msg })
            : t('cameras.flowSettings.zonesSaveError'))
          isSaving.value = false
          return
        }
        
        try {
          ///Put this as last. For some reason it mounts the page after execution, resetting the values in the detection zone
          await flowStore.editFlow(flowStore.currentFlow.FlowId, flowStore.currentFlow.FlowName, flowStore.currentFlow.InputTypeName, userStore.user.UserId)
          ////End strange behaviour
        } catch (error) {
          console.error("Error updating Flow table:", error)
                        toast.error(t('cameras.flowSettings.flowSaveError'), error)
        }

        // Update original values after successful save
        cameraMoved.value = false
        originalFlowName.value = flowStore.currentFlow.FlowName
        originalDetectionZones.value = JSON.stringify(
          Object.entries(DetectionZone.value || {}).map(([name, zone]) => ({
            name,
            type: zone.type,
            points: zone.points,
            tripwire: zone.tripwire || null,
            tripwires: Array.isArray(zone.tripwires) ? zone.tripwires : [],
            settings: zone.settings,
          }))
        )
        
        toast.success(t('cameras.flowSettings.updated'))

        // Clear or update local storage to ensure fresh data
        localStorage.removeItem('listOfFlows')
        localStorage.removeItem('FixedWidgetsData')
        localStorage.removeItem('detectionZoneData')

        await flowStore.fetchFlows()
        await detectionzones.fetchDetectionZones()

        isSaving.value = false
        router.push(`/flow/${flowStore.currentFlow.FlowId}`)
      } catch (error) {
        console.error("An unexpected error occurred while writing data to the database.:", error)
        toast.error(t('cameras.saveError'), error)
        isSaving.value = false
      }
    }

    const deleteFlow = async () => {
      try {
        isSaving.value = true
        if (confirm(t('cameras.flowSettings.confirmDelete'))) {
                await flowStore.deleteFlow(flowStore.currentFlow.FlowId)
                
                // Clear or update local storage to ensure fresh data
                localStorage.removeItem('listOfFlows')
                localStorage.removeItem('FixedWidgetsData')
                localStorage.removeItem('detectionZoneData')

                await flowStore.fetchFlows()
                toast.info(t('cameras.flowSettings.deleted'))
                isSaving.value = false
                setTimeout(() => {
                    router.push('/camera_overview')
                }, 100) // Add a slight delay before navigation
            }
      } catch (error) {
        console.error("An unexpected error occurred while deleting the camera view.", error)
        toast.error(t('cameras.flowSettings.deleteError'), error)
        isSaving.value = false
      }
    }

    const backHome = () => {
      router.push(`/flow/${flowStore.currentFlow.FlowId}`)
    }

    return { changeQuestion, answerChangeQuestion, cameraMoved,
      flowStore,
      editName,
      openNameEdit,
      DetectionZone,
      cameraStreamLink,
      cameraStreamId,
      isCameraRunning,
      on_detectionzone_logic,
      saveEditedFlow,
      deleteFlow,
      backHome,
      capturedFrame,
      capturedFrameString,
      isLoading,
      isSaving,
      showNotification,
      onCancel,
      hasChanges
      }
  }
}
</script>

<style scoped>
/* ====== Header ====== */
.section-head{
  display:flex; justify-content:space-between; align-items:flex-start; gap:12px; flex-wrap:wrap;
  border-left:6px solid var(--accent,#4F7EB3);
  background:#fff; border:1px solid #e6edf3; border-radius:12px;
  padding:12px 14px; margin:10px 0 14px;
  box-shadow:0 3px 12px rgba(13,38,76,.06);
}
.title{ margin:0; font-weight:800; color:#425461; display:flex; align-items:center; gap:8px }
.icon-inline{
  border:1px solid #e6edf3; background:#fff; color:#4F7EB3;
  width:32px; height:32px; border-radius:8px; display:inline-flex; align-items:center; justify-content:center; cursor:pointer;
}
.icon-inline:hover{ background:#f4f9ff }
.head-left{ display:flex; flex-direction:column; gap:10px }
.head-actions{ display:flex; gap:10px; flex-wrap:wrap }

.name-edit{ max-width:520px }
.input{
  width:100%; border:1px solid #dfe7ee; border-radius:10px; padding:10px 12px; background:#fff; color:#2b3a44; outline:none;
}
.input.center{ text-align:center }
.input:focus{ border-color:#4F7EB3; box-shadow:0 0 0 3px rgba(79,126,179,.12) }

.banner-warn{
  display:inline-block; background:#fff7e6; color:#b66a00; border:1px solid #ffe4b0;
  padding:8px 10px; border-radius:10px; font-size:.9rem; font-weight:600;
}

/* ====== Card ====== */
.kpi-accent{ position:relative; border:none; box-shadow:0 4px 18px rgba(13,38,76,.06); border-radius:14px }
.kpi-accent::before{
  content:""; position:absolute; inset:0 auto 0 0; width:6px; background:var(--accent,#4F7EB3);
  border-radius:14px 0 0 14px;
}
.form-head{
  display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef3f7;
}
.form-title{ margin:0; color:#4F7EB3; font-weight:800 }
.card-body{ padding:14px 16px }

/* camera stream read-only block */
.read-block{ margin-bottom:12px }
.read-label{ display:block; font-weight:700; color:#425461; margin-bottom:4px }
.read-value{ margin:0; background:#f8fbff; border:1px dashed #e6edf3; border-radius:10px; padding:10px 12px }
.muted{ color:#6b7a86; word-break:break-all }

/* DZ area */
.dz-wrap{ margin-top:12px }

/* footer */
.form-actions{
  display:flex; gap:10px; align-items:center; justify-content:flex-end;
  padding:12px 16px; border-top:1px solid #eef3f7;
}
.saving{ margin-right:auto; display:flex; align-items:center; gap:8px; color:#6b7a86 }
.camera-moved{ display:flex; align-items:center; gap:6px; margin:0 6px 0 0; font-size:.9rem;
               color:#2C5F86; cursor:pointer; }

/* Drawing/adding zones is hidden on small screens (see DetectionZone), so the
   flow's Save is hidden there too — zone-setting changes persist via the
   settings modal's own Save. */
@media (max-width: 1024px){
  .hide-on-small{ display:none !important; }
}

/* ====== Buttons ====== */
.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; background:#fff;
  padding:8px 12px; border-radius:10px; cursor:pointer; text-decoration:none;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }

.btn-solid{
  display:inline-flex; align-items:center; gap:8px; border:0; color:#fff;
  padding:8px 14px; border-radius:10px; cursor:pointer;
}
.btn-solid.ok{ background:#6C9046 }
.btn-solid.danger{ background:#C21500 }
.btn-solid:disabled{ opacity:.55; cursor:not-allowed }

/* ====== Loaders ====== */
.loader-wrap{ display:flex; justify-content:center; padding:16px 0 }
.loader{
  width:80px; padding:8px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
.saver{
  width:22px; padding:2px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }
</style>